import type { InspectionSessionRecord } from '@navio/nsdk';
import {
  getUserDisplayName,
  resolveObservations,
  UNKNOWN_USER_NAME,
  WHEEL_LABELS,
  type ResolvedObservations,
  type ResolvedSpecification,
  type WheelField
} from 'noddi-util';

import type { InspectionCardProps } from './InspectionCard';
import { getInspectionCardTireDimensions } from './inspectionTireDimensions';
import { type WheelPhoto } from './WheelPhotoGallery';

const dimensionItems = (specifications: ResolvedSpecification[]) =>
  specifications.map((specification) => ({
    location: specification.location,
    tire: { specification: specification.specification }
  }));

export const inspectionCardPropsFromResolved = (
  resolved: ResolvedObservations<InspectionSessionRecord>,
  wheelLabels: Record<WheelField, string> = WHEEL_LABELS
): Pick<
  InspectionCardProps,
  'season' | 'tireDimensions' | 'wheels' | 'inspectionContext' | 'inspectionDate' | 'inspectedBy'
> => {
  const inspectedByName = resolved.session?.createdBy ? getUserDisplayName(resolved.session.createdBy) : undefined;
  const wheels: WheelPhoto[] = resolved.wheels.map((wheel) => ({
    position: wheel.field,
    label: wheelLabels[wheel.field],
    imageUrl: wheel.photo?.imageUrl ?? null
  }));

  return {
    season: resolved.season,
    tireDimensions: getInspectionCardTireDimensions(dimensionItems(resolved.specifications)),
    wheels,
    inspectionContext: resolved.session?.context,
    inspectionDate: resolved.session?.inspectedAt,
    // A nameless user reads as a bare dash; the card leaves the field out instead.
    inspectedBy: inspectedByName === UNKNOWN_USER_NAME ? undefined : inspectedByName
  };
};

export const inspectionCardPropsFromSession = (
  session: InspectionSessionRecord,
  wheelLabels: Record<WheelField, string> = WHEEL_LABELS
) => inspectionCardPropsFromResolved(resolveObservations([session]), wheelLabels);
